import {
  useRef,
  useCallback,
  useMemo,
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
  memo,
} from 'react';
import { useControl } from 'react-map-gl';
import { MapboxDrawOptions, constants } from '@mapbox/mapbox-gl-draw';

import MapControlBar, {
  MapControlBaseEventHandler,
  MapControlBarButtons,
} from '~utils/mapClasses/ControlBar';
import { createFeatureCollection } from '~utils/geoJson';
import { controlLocale } from '~constants/mapControlLocale';
import { Nullable } from '~globals/types';

import {
  DrawPolygonControlRef,
  DrawPolygonControlProps,
  DrawPolygonControlFeatures,
} from './types';
import { DEFAULT_STYLES } from './utils';

const DEFAULT_SELECTION_BAR_PROPS: MapboxDrawOptions = {
  userProperties: true,
  displayControlsDefault: false,
  controls: { polygon: true, trash: true },
  defaultMode: 'draw_polygon',
  keybindings: false,
  styles: DEFAULT_STYLES,
};

const DrawPolygonControl = forwardRef<
  DrawPolygonControlRef,
  DrawPolygonControlProps
>(function DrawPolygonControl(inProps, ref): null {
  const {
    position,
    onCreate,
    onUpdate,
    onDelete,
    color = '#fbb03b',
    defaultValue,
  } = inProps;

  const selectionBarRef = useRef<Nullable<MapControlBar>>(null);
  const [lastUpdateCount, setLasUpdateCoun] = useState(0);
  const [isDrawing, setIsDrawing] = useState(true);

  const forceUpdate = useCallback(() => {
    setLasUpdateCoun((prevLastUpdateCount) => prevLastUpdateCount + 1);
  }, []);

  const handlerCreateDraw = useCallback<
    MapControlBaseEventHandler<DrawPolygonControlFeatures>
  >(
    (event, drawInstance) => {
      drawInstance?.disabledButton('.mapbox-gl-draw_polygon');

      onCreate?.(event, drawInstance);
    },
    [onCreate],
  );

  const handlerUpdateDraw = useCallback<
    MapControlBaseEventHandler<DrawPolygonControlFeatures>
  >(
    (event, drawInstance) => {
      onUpdate?.(event, drawInstance);
    },
    [onUpdate],
  );

  const handlerDeleteDraw = useCallback<
    MapControlBaseEventHandler<DrawPolygonControlFeatures>
  >(
    (event, drawInstance) => {
      drawInstance?.enabledButton('.mapbox-gl-draw_polygon');

      onDelete?.(event, drawInstance);
    },
    [onDelete],
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChangeMode = useCallback<MapControlBaseEventHandler<any>>(
    (event) => {
      forceUpdate();

      setIsDrawing(
        event.mode === constants.modes.DRAW_POLYGON ||
          event.mode === constants.modes.DIRECT_SELECT,
      );
    },
    [forceUpdate],
  );

  const buttons = useMemo<MapControlBarButtons>(
    () => ({
      core: [
        {
          selector: '.mapbox-gl-draw_polygon',
          title: controlLocale['Draw.Polygon'],
        },
        {
          selector: '.mapbox-gl-draw_trash',
          title: controlLocale.Delete,
        },
      ],
    }),
    [],
  );

  useControl<MapControlBar>(
    ({ map }) => {
      const drawCtrl = new MapControlBar(DEFAULT_SELECTION_BAR_PROPS, buttons);

      selectionBarRef.current = drawCtrl;

      map.on('draw.create', (event) => handlerCreateDraw(event, drawCtrl));
      map.on('draw.update', (event) => handlerUpdateDraw(event, drawCtrl));
      map.on('draw.delete', (event) => handlerDeleteDraw(event, drawCtrl));
      map.on('draw.modechange', (event) => handleChangeMode(event, drawCtrl));
      map.on('draw.selectionchange', forceUpdate);
      map.on('draw.actionable', forceUpdate);

      return drawCtrl;
    },
    ({ map }) => {
      selectionBarRef.current = null;

      map.off('draw.create', (event) =>
        handlerCreateDraw(event, selectionBarRef.current),
      );
      map.off('draw.update', (event) =>
        handlerUpdateDraw(event, selectionBarRef.current),
      );
      map.off('draw.delete', (event) =>
        handlerDeleteDraw(event, selectionBarRef.current),
      );
      map.off('draw.modechange', (event) =>
        handleChangeMode(event, selectionBarRef.current),
      );
      map.off('draw.selectionchange', forceUpdate);
      map.off('draw.actionable', forceUpdate);
    },
    { position },
  );

  useEffect(() => {
    if (defaultValue) {
      selectionBarRef.current?.ctrl.set(
        createFeatureCollection([defaultValue]),
      );

      selectionBarRef.current?.ctrl.changeMode('simple_select');
      selectionBarRef.current?.disabledButton('.mapbox-gl-draw_polygon');
      setIsDrawing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const currentFeatures = selectionBarRef.current?.ctrl.getAll().features;

    currentFeatures?.forEach((feature) => {
      feature.properties = { currentColor: color };

      selectionBarRef.current?.ctrl.add(feature);
    });
  }, [color, lastUpdateCount]);

  useImperativeHandle(ref, () => ({ isDrawing, forceUpdate }), [
    isDrawing,
    forceUpdate,
  ]);

  return null;
});

export default memo(DrawPolygonControl);
